/**
 * Глобальный поиск людей по никнейму (`GET /users/search`, BS §11.3) — вторая секция
 * поиска в списке чатов, как в Telegram. Локальный фильтр по диалогам остаётся у экрана;
 * сюда приходит уже отобранный запрос (`usernameSearchQuery`) или `null`.
 */
import { useEffect, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import type { Channel, UserSearchResult } from '@shared/api/types'

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_LIMIT = 20

interface SearchState {
  items: UserSearchResult[]
  loading: boolean
}

export function useUsernameSearch(query: string | null): SearchState {
  const [state, setState] = useState<SearchState>({ items: [], loading: false })

  useEffect(() => {
    if (!query) {
      setState({ items: [], loading: false })
      return
    }
    let cancelled = false
    setState((current) => ({ ...current, loading: true }))
    const timer = setTimeout(() => {
      void api
        .GET('/users/search', { params: { query: { q: query, limit: SEARCH_LIMIT } as never } })
        .then((result) => unwrap(result))
        .then((page) => {
          if (!cancelled) setState({ items: page.items, loading: false })
        })
        .catch(() => {
          // Ошибка сети или лимита не должна ломать локальный поиск — секция просто пустая.
          if (!cancelled) setState({ items: [], loading: false })
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  return state
}

/**
 * Поиск публичных каналов (`GET /channels/search`, изменение v1.7) — третья секция поиска,
 * как «Channels» в глобальном поиске Telegram. Порог и отбор запроса те же, что у людей:
 * сюда приходит уже готовый запрос или `null`.
 */
export function useChannelSearch(query: string | null): { items: Channel[]; loading: boolean } {
  const [state, setState] = useState<{ items: Channel[]; loading: boolean }>({
    items: [],
    loading: false,
  })

  useEffect(() => {
    if (!query) {
      setState({ items: [], loading: false })
      return
    }
    let cancelled = false
    setState((current) => ({ ...current, loading: true }))
    const timer = setTimeout(() => {
      void api
        .GET('/channels/search', { params: { query: { q: query, limit: SEARCH_LIMIT } as never } })
        .then((result) => unwrap(result))
        .then((page) => {
          if (!cancelled) setState({ items: page.items, loading: false })
        })
        .catch(() => {
          if (!cancelled) setState({ items: [], loading: false })
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  return state
}
